import { useRef, useState, useCallback } from 'react';

export interface UseHistoryOptions<T> {
  /** Deep-clone function applied when pushing or reading snapshots. */
  clone: (value: T) => T;
  /** Maximum retained history length. Defaults to 50. */
  maxHistory?: number;
  /** Idle delay (ms) before a debounced markDirty gets committed. Defaults to 600. */
  idleDelayMs?: number;
}

export interface UseHistoryReturn<T> {
  markDirty: (current: T) => void;
  undo: (current: T) => T | null;
  redo: (current: T) => T | null;
  clear: () => void;
  flush: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Generic undo/redo stack with debounced markDirty. Rapid calls within
 * `idleDelayMs` are coalesced into a single history entry.
 */
export function useHistory<T>(options: UseHistoryOptions<T>): UseHistoryReturn<T> {
  const { clone, maxHistory = 50, idleDelayMs = 600 } = options;

  const pastRef = useRef<T[]>([]);
  const futureRef = useRef<T[]>([]);
  const [, setRevision] = useState(0);

  const pendingSnapshotRef = useRef<T | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const bump = useCallback(() => setRevision((r) => r + 1), []);

  const pushSnapshot = useCallback(
    (snapshot: T) => {
      pastRef.current.push(clone(snapshot));
      if (pastRef.current.length > maxHistory) {
        pastRef.current.shift();
      }
      futureRef.current = [];
      bump();
    },
    [bump, clone, maxHistory],
  );

  const flush = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (pendingSnapshotRef.current !== null) {
      pushSnapshot(pendingSnapshotRef.current);
      pendingSnapshotRef.current = null;
    }
  }, [pushSnapshot]);

  const markDirty = useCallback(
    (currentSnapshot: T) => {
      if (pendingSnapshotRef.current === null) {
        pendingSnapshotRef.current = clone(currentSnapshot);
      }
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        idleTimerRef.current = null;
        if (pendingSnapshotRef.current !== null) {
          pushSnapshot(pendingSnapshotRef.current);
          pendingSnapshotRef.current = null;
        }
      }, idleDelayMs);
    },
    [clone, idleDelayMs, pushSnapshot],
  );

  const undo = useCallback(
    (current: T): T | null => {
      if (pendingSnapshotRef.current !== null) {
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
          idleTimerRef.current = null;
        }
        pushSnapshot(pendingSnapshotRef.current);
        pendingSnapshotRef.current = null;
      }

      if (pastRef.current.length === 0) return null;
      const snapshot = pastRef.current.pop()!;
      futureRef.current.push(clone(current));
      bump();
      return snapshot;
    },
    [bump, clone, pushSnapshot],
  );

  const redo = useCallback(
    (current: T): T | null => {
      if (futureRef.current.length === 0) return null;
      const snapshot = futureRef.current.pop()!;
      pastRef.current.push(clone(current));
      if (pastRef.current.length > maxHistory) {
        pastRef.current.shift();
      }
      bump();
      return snapshot;
    },
    [bump, clone, maxHistory],
  );

  const clear = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    pendingSnapshotRef.current = null;
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    bump();
  }, [bump]);

  const canUndo = pastRef.current.length > 0 || pendingSnapshotRef.current !== null;
  const canRedo = futureRef.current.length > 0;

  return { markDirty, undo, redo, clear, flush, canUndo, canRedo };
}
